/**
 * Utility functions for fetching user data for email notifications
 * This file centralizes user data fetching to avoid code duplication
 */

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType?: string;
  [key: string]: any;
}

/**
 * Fetch user data by user ID
 * @param userId - User ID to fetch
 * @param token - Authentication token
 * @returns User data or null if not found
 */
export const fetchUserData = async (
  userId: string | null | undefined,
  token: string | null
): Promise<UserData | null> => {
  if (!userId || !token) {
    return null;
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch user data for userId: ${userId}`, response.statusText);
      return null;
    }

    const data = await response.json();
    return {
      id: data?.id || data?._id || userId,
      email: data?.email || "",
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      licenseType: data?.licenseType || "",
      ...data,
    };
  } catch (error) {
    console.error(`Error fetching user data for userId: ${userId}`, error);
    return null;
  }
};

/**
 * Fetch multiple users' data in parallel
 * @param userIds - Array of user IDs to fetch
 * @param token - Authentication token
 * @returns Map of userId to UserData
 */
export const fetchMultipleUsersData = async (
  userIds: (string | null | undefined)[],
  token: string | null
): Promise<Map<string, UserData>> => {
  const userMap = new Map<string, UserData>();
  
  if (!token || userIds.length === 0) {
    return userMap;
  }

  // Filter out null/undefined and duplicates
  const validIds = Array.from(new Set(userIds.filter((id): id is string => !!id)));
  
  if (validIds.length === 0) {
    return userMap;
  }

  // Fetch all users in parallel
  const fetchPromises = validIds.map(async (userId) => {
    const userData = await fetchUserData(userId, token);
    if (userData) {
      userMap.set(userId, userData);
    }
    return userData;
  });

  await Promise.allSettled(fetchPromises);
  
  return userMap;
};

/**
 * Customer Group interface
 */
export interface CustomerGroup {
  _id: string;
  name: string;
  email: string;
  payment_after_days: number;
  allow_partial_pay: boolean;
  max_credit_limit: number;
  allowed_user_ids: Array<{
    user_id: string;
    name: string;
    email: string;
  }>;
  status: string;
  created_by: {
    id: string;
    name: string;
  };
  updated_by: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

/**
 * Fetch all customer groups
 * @param token - Authentication token
 * @returns Array of customer groups or empty array if error
 */
export const fetchCustomerGroups = async (
  token: string | null
): Promise<CustomerGroup[]> => {
  if (!token) {
    return [];
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch customer groups`, response.statusText);
      return [];
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`Error fetching customer groups`, error);
    return [];
  }
};

/**
 * Get all unique emails from customer groups' allowed_user_ids
 * @param token - Authentication token
 * @returns Array of unique email addresses
 */
export const getCustomerGroupEmails = async (
  token: string | null
): Promise<string[]> => {
  const groups = await fetchCustomerGroups(token);
  const emailSet = new Set<string>();
  
  groups.forEach((group) => {
    if (Array.isArray(group.allowed_user_ids)) {
      group.allowed_user_ids.forEach((user) => {
        if (user?.email && user.email.trim()) {
          emailSet.add(user.email.trim());
        }
      });
    }
  });
  
  return Array.from(emailSet);
};

/**
 * Get customer group users with their data (email and name)
 * @param token - Authentication token
 * @returns Array of user info objects with email and name
 */
export const getCustomerGroupUsers = async (
  token: string | null
): Promise<Array<{ email: string; name: string; userId: string }>> => {
  const groups = await fetchCustomerGroups(token);
  const userMap = new Map<string, { email: string; name: string; userId: string }>();
  
  groups.forEach((group) => {
    if (Array.isArray(group.allowed_user_ids)) {
      group.allowed_user_ids.forEach((user) => {
        if (user?.email && user.email.trim() && user?.user_id) {
          const email = user.email.trim();
          if (!userMap.has(email)) {
            userMap.set(email, {
              email: email,
              name: user.name || email.split("@")[0],
              userId: user.user_id,
            });
          }
        }
      });
    }
  });
  
  return Array.from(userMap.values());
};

/**
 * Fetch customer group by domain
 * @param supplierId - Supplier ID (optional, can be empty string)
 * @param domain - Domain name (e.g., "warehouse")
 * @param token - Authentication token
 * @returns Customer group data or null if not found
 */
export const fetchCustomerGroupByDomain = async (
  supplierId: string | null | undefined,
  domain: string,
  token: string | null
): Promise<CustomerGroup | null> => {
  if (!domain || !token) {
    return null;
  }

  try {
    // Build URL - supplierId might be needed as query parameter or in path, adjust based on actual API
    const baseUrl = `${process.env.REACT_APP_API_URL}/customer-groups/${supplierId}/by-domain/${domain}`;
    const url = supplierId ? `${baseUrl}` : baseUrl;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Failed to fetch customer group by domain: ${domain}`, response.statusText);
      return null;
    }

    const data = await response.json();
    return data || null;
  } catch (error) {
    console.error(`Error fetching customer group by domain: ${domain}`, error);
    return null;
  }
};

/**
 * Parent hierarchy response interface
 */
export interface ParentHierarchy {
  parent_chain?: Array<{
    id?: string;
    _id?: string;
    name: string;
    license_type: string;
  }>;
}

/**
 * Fetch parent hierarchy and find signage firm parent
 * @param token - Authentication token
 * @returns Object with parentSignageId and parentSignageName, or null if not found
 */
export const fetchParentHierarchy = async (
  token: string | null
): Promise<{ parentSignageId: string | null; parentSignageName: string | null } | null> => {
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.warn(`Failed to fetch parent hierarchy`, response.statusText);
      return null;
    }

    const data: ParentHierarchy = await response.json();

    // Find the parent with license_type "SIGNAGE_FIRM"
    const signageFirm = data.parent_chain?.find(
      (parent) => parent.license_type === "SIGNAGE_FIRM"
    );

    if (signageFirm) {
      return {
        parentSignageId: signageFirm.id || signageFirm._id || null,
        parentSignageName: signageFirm.name || null,
      };
    }

    return null;
  } catch (error) {
    console.error(`Error fetching parent hierarchy`, error);
    return null;
  }
};

/**
 * Office Team Member interface
 */
export interface OfficeTeamMember {
  _id: string;
  vid?: number;
  email: string;
  userType: string;
  firstName: string;
  lastName: string;
  phone1?: string;
  phone2?: string;
  jobTitle?: string;
  licenseType: string;
  status: string;
  offices?: Array<{
    _id: string;
    officeName: string;
    officeType?: string;
    officeAddress?: any;
    status: boolean;
  }>;
  warehouses?: any;
  firmDetails?: any;
  agentDetails?: any;
  installerInfo?: any;
  [key: string]: any;
}

/**
 * Fetch brokerage team members by office ID
 * @param officeId - Office ID to fetch team members for
 * @param token - Authentication token
 * @returns Array of team member data or empty array if error
 */
export const fetchOfficeTeamMembers = async (
  officeId: string | null | undefined,
  token: string | null
): Promise<OfficeTeamMember[]> => {
  if (!officeId || !token) {
    return [];
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/office/${officeId}/team-members`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 400) {
        console.warn(`Invalid office ID: ${officeId}`);
        return [];
      }
      if (response.status === 404) {
        console.warn(`Office not found: ${officeId}`);
        return [];
      }
      console.warn(`Failed to fetch office team members for officeId: ${officeId}`, response.statusText);
      return [];
    }

    const data = await response.json();
    return Array.isArray(data?.data) ? data.data : [];
  } catch (error) {
    console.error(`Error fetching office team members for officeId: ${officeId}`, error);
    return [];
  }
};

/**
 * Get all unique emails from office team members
 * @param officeId - Office ID to fetch team members for
 * @param token - Authentication token
 * @returns Array of unique email addresses
 */
export const getOfficeTeamMemberEmails = async (
  officeId: string | null | undefined,
  token: string | null
): Promise<string[]> => {
  const teamMembers = await fetchOfficeTeamMembers(officeId, token);
  const emailSet = new Set<string>();
  
  teamMembers.forEach((member) => {
    if (member?.email && member.email.trim()) {
      emailSet.add(member.email.trim());
    }
  });
  
  return Array.from(emailSet);
};
